import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, GraduationCap } from 'lucide-react'

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()

  const isHome = location.pathname === '/'
  const headerScrolled = isHome ? isScrolled : true

  const navigation = [
    { name: 'Inicio', href: '/' },
    { name: 'Nosotros', href: '/nosotros' },
    { name: 'ExpoGardenias', shortName: 'Expo', href: '/expogardenias' },
    { name: 'Areas', href: '/areas' },
    { name: 'Bienestar', href: '/bienestar' },
    { name: 'Revistas', href: '/revistas' },
    { name: 'Admisiones', href: '/admisiones' },
    { name: 'Contacto', href: '/contacto' },
  ]

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMenuOpen(false)
  }, [location])

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${headerScrolled
      ? 'bg-white shadow-lg backdrop-blur-sm'
      : 'bg-transparent'
      }`}>
      <nav className="relative w-full">
        <div className={`transition-all duration-300 ${headerScrolled
          ? 'h-16 sm:h-20 md:h-24'
          : 'h-24 sm:h-32 md:h-40 lg:h-48'
          } relative z-50`}>

          {/* Contenedor flex real: logo a la izquierda, menú a la derecha */}
          <div className="container-custom flex items-center justify-between h-full gap-2 sm:gap-3">

            {/* Logo - se compacta en el rango lg (1024-1279) para dejarle sitio al
                menú de escritorio; respira de nuevo desde xl (1280+). */}
            <Link to="/" className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-3 lg:space-x-2 group min-w-0">
              <div className="relative flex-shrink-0">
                <img
                  src="/images/logo.png"
                  alt="Logo Las Gardenias"
                  className={`object-contain group-hover:scale-110 transition-all duration-300 ${headerScrolled
                      ? 'h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 lg:h-20 lg:w-20 xl:h-14 xl:w-14'
                      : 'h-16 w-16 sm:h-24 sm:w-24 md:h-28 md:w-28 lg:h-24 lg:w-24 xl:h-20 xl:w-20'
                    }`}
                  style={{ background: 'transparent' }}
                />
              </div>

              <div className="flex flex-col relative min-w-0 group-hover:scale-110 transition-all duration-300">
                <span className={`font-bold text-gardenia-red drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)] whitespace-nowrap ${headerScrolled
                    ? 'text-xs sm:text-base md:text-lg lg:text-2xl xl:text-xl'
                    : 'text-sm sm:text-xl md:text-2xl lg:text-4xl xl:text-3xl'
                  }`}>
                  Las Gardenias
                </span>
                <span className={`font-medium whitespace-nowrap ${headerScrolled
                    ? 'text-[10px] sm:text-xs lg:text-base xl:text-sm text-gardenia-dark'
                    : 'text-xs sm:text-sm lg:text-xl xl:text-lg text-white text-shadow-black'
                  }`}>
                  Paz, Educación y Progreso
                </span>
              </div>
            </Link>

            {/* Menú - item de flujo normal con ancho garantizado (no se encoge) */}
            <div className="flex items-center flex-shrink-0">
              {/* Desktop Navigation - desde lg (1024px), como antes */}
              <div className="hidden lg:flex items-center">
                <div className="flex items-center lg:gap-0.5 xl:gap-2">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`relative lg:px-1.5 xl:px-3 py-2 rounded-full font-medium transition-all duration-500 ease-in-out overflow-hidden group lg:text-[11px] xl:text-sm whitespace-nowrap ${location.pathname === item.href
                        ? 'bg-gardenia-red text-white shadow-lg scale-105'
                        : headerScrolled
                          ? 'text-gardenia-dark hover:text-white'
                          : 'text-white text-shadow-black hover:text-white'
                        }`}
                    >
                      <span className={`absolute inset-0 bg-gardenia-red rounded-full transform transition-all duration-500 ease-in-out ${location.pathname === item.href
                        ? 'scale-100 opacity-100'
                        : 'scale-0 opacity-0 group-hover:scale-100 group-hover:opacity-100'
                        }`}></span>

                      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out"></span>

                      <span className="relative z-10">
                        <span className="xl:hidden">{item.shortName || item.name}</span>
                        <span className="hidden xl:inline">{item.name}</span>
                      </span>
                    </Link>
                  ))}
                  <Link
                    to="/admisiones"
                    className="btn-primary lg:ml-1 xl:ml-3 lg:text-[11px] xl:text-sm whitespace-nowrap lg:px-2.5 xl:px-6"
                  >
                    Admisiones <span className="hidden xl:inline">2026</span>
                  </Link>
                </div>
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`lg:hidden p-2 rounded-lg transition-colors duration-300 ${headerScrolled
                  ? 'text-gardenia-dark hover:bg-gardenia-gold/20'
                  : 'text-white hover:bg-white/20'
                  }`}
                aria-expanded={isMenuOpen}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <X className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                ) : (
                  <Menu className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}

<div className={`lg:hidden transition-all duration-300 ${
  isMenuOpen 
    ? 'max-h-[70vh] opacity-100 overflow-y-auto' 
    : 'max-h-0 opacity-0 overflow-hidden'
  }`}>
  <div className="px-4 py-4 space-y-2 border-t border-gardenia-gold/30 bg-white/95 backdrop-blur-sm">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`relative block px-4 py-3 rounded-lg font-medium transition-all duration-300 overflow-hidden group text-sm ${location.pathname === item.href
                  ? 'bg-gardenia-red text-white shadow-lg'
                  : 'text-gardenia-dark hover:text-white hover:bg-gardenia-red/10'
                  }`}
              >
                <span className={`absolute inset-0 bg-gardenia-red rounded-lg transform transition-all duration-300 ${location.pathname === item.href
                  ? 'scale-100 opacity-100'
                  : 'scale-0 opacity-0 group-hover:scale-100 group-hover:opacity-100'
                  }`}></span>

                <span className="relative z-10">
                  {item.name}
                </span>
              </Link>
            ))}
            <Link
              to="/admisiones"
              className="block btn-primary text-center mt-4 text-sm"
            >
              Admisiones 2026
            </Link>
          </div>
        </div>
      </nav>
    </header>
  )
}

export default Header